"use client";

import { parseError } from "evlog";
import { useCallback, useRef, useState } from "react";

import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldTitle,
} from "#/components/ui/field";
import { Spinner } from "#/components/ui/spinner";
import { Switch } from "#/components/ui/switch";

function useToggleSave({
  initialEnabled,
  persist,
}: {
  initialEnabled: boolean;
  persist: (enabled: boolean) => Promise<void>;
}) {
  const savedValueRef = useRef(initialEnabled);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const onCheckedChange = useCallback(
    (value: boolean) => {
      if (isSaving || value === savedValueRef.current) return;

      setEnabled(value);
      setIsSaving(true);
      setSaveError(null);

      void persist(value)
        .then(() => {
          savedValueRef.current = value;
        })
        .catch((err: unknown) => {
          setEnabled(savedValueRef.current);
          setSaveError(parseError(err).message);
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    [isSaving, persist],
  );

  return { enabled, isSaving, saveError, onCheckedChange };
}

function ToggleSwitch({
  checked,
  disabled,
  hasError,
  onCheckedChange,
  switchId,
  title,
}: {
  checked: boolean;
  disabled: boolean;
  hasError: boolean;
  onCheckedChange: (checked: boolean) => void;
  switchId: string;
  title: string;
}) {
  return (
    <span className="flex shrink-0 items-center gap-2 self-center">
      {disabled ? <Spinner className="text-muted-foreground" /> : null}
      <Switch
        id={switchId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={title}
        aria-invalid={hasError}
        aria-busy={disabled}
      />
    </span>
  );
}

export function ProfileToggleSetting({
  description,
  initialEnabled,
  persist,
  switchId,
  title,
}: {
  description: string;
  initialEnabled: boolean;
  persist: (enabled: boolean) => Promise<void>;
  switchId: string;
  title: string;
}) {
  const { enabled, isSaving, saveError, onCheckedChange } = useToggleSave({
    initialEnabled,
    persist,
  });
  const hasError = saveError !== null;

  return (
    <Field
      orientation="horizontal"
      className="items-center justify-between gap-4"
      data-invalid={hasError}
    >
      <FieldContent className="min-w-0 flex-1">
        <FieldTitle>{title}</FieldTitle>
        <FieldDescription>{description}</FieldDescription>
        {hasError ? <FieldError>{saveError}</FieldError> : null}
      </FieldContent>
      <ToggleSwitch
        checked={enabled}
        disabled={isSaving}
        hasError={hasError}
        onCheckedChange={onCheckedChange}
        switchId={switchId}
        title={title}
      />
    </Field>
  );
}
